import { MARK_INSERT_REVISION } from './InsertRevision';
import { MARK_DELETE_REVISION } from './DeleteRevision';
import { MARK_FORMAT_REVISION } from './FormatRevision';
import { MARK_MOVE_FROM_REVISION } from './MoveFromRevision';
import { MARK_MOVE_TO_REVISION } from './MoveToRevision';

/**
 * 겹친 리비전(변경 추적) 표시 중 어느 것이 "최신"인지 고르는 유일한 정본 규칙.
 * ProseMirror `Mark`(에디터 미리보기 데코레이션)와 BE JSON 마크(계약서 검토 위험 카드·Apply 물질화)
 * 양쪽이 서로 다른 자료형을 쓰므로, 이 함수는 어느 쪽 자료형에도 묶이지 않는 (item, createdAt) 계약만 안다.
 *
 * createdAt이 없거나 파싱 불가능하면(생성 타입상 필수라 실 BE 데이터에서는 나타나지 않아야 하는 경계 위반)
 * 조용히 건너뛰거나 NaN 비교로 흘리지 않고 즉시 던진다 — 예전 구현은 invalid date가 `NaN`으로 굳어버리면
 * `latestTime <= time` 비교가 항상 false가 되어 이후 valid 값이 와도 절대 갱신되지 않는 결함이 있었다.
 *
 * 동시각(tie)은 배열 순회 순서에 기대지 않는다 — 원문(raw) BE 배열 순서와 ProseMirror가 스키마 rank로
 * 재정렬한 순서가 서로 달라, 순서 기반 tie-break는 같은 겹침을 서로 다른 승자로 만들 수 있다. 대신
 * `getTieBreakRank`(마크 타입 기반, 순서 무관)로 결정한다 — 값이 작을수록 이긴다.
 */
export function pickLatestByCreatedAt<T>(items: readonly T[], getCreatedAt: (item: T) => string | undefined, getTieBreakRank: (item: T) => number): T | undefined {
    let latest: T | undefined;
    let latestTime = -Infinity;
    let latestRank = Infinity;
    for (const item of items) {
        const createdAt = getCreatedAt(item);
        const time = new Date(createdAt as string).getTime();
        if (Number.isNaN(time)) throw new Error(`pickLatestByCreatedAt: revision mark의 createdAt이 없거나 유효하지 않습니다: ${JSON.stringify(createdAt)}`);
        const rank = getTieBreakRank(item);
        if (latest === undefined || time > latestTime || (time === latestTime && rank < latestRank)) {
            latest = item;
            latestTime = time;
            latestRank = rank;
        }
    }
    return latest;
}

/**
 * revision mark 타입별 동시각(tie) 승자 우선순위 — 값이 작을수록 이긴다. 배열 순서와 무관한 고정 규칙이라
 * 원문(raw) BE 매핑과 ProseMirror 미리보기가 항상 같은 승자를 고른다. delete류가 insert류·format보다
 * 앞선다 — 동시각처럼 사실상 구분 불가능한 충돌에서는 내용을 잃지 않는(숨기는) 쪽을 우선한다.
 */
export const REVISION_MARK_TYPE_TIE_BREAK_RANK: Readonly<Record<string, number>> = {
    [MARK_DELETE_REVISION]: 0,
    [MARK_MOVE_FROM_REVISION]: 1,
    [MARK_MOVE_TO_REVISION]: 2,
    [MARK_INSERT_REVISION]: 3,
    [MARK_FORMAT_REVISION]: 4,
};
